import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Box,
  Typography,
  InputBase,
  Switch,
  Button,
  IconButton,
  CircularProgress,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import IosShareIcon from "@mui/icons-material/IosShare";

import ApiService from "../../services/api-service";
import JukeboxDaySubmissions from "./JukeboxDaySubmissions";
import notifier from "../Notifier";
import { getErrorString } from "../../constants/function";
import {
  combineLocal,
  dateInputBounds,
  formatSessionDate,
  jukeboxDayUrl,
  sessionKey,
  shareLink,
  submissionSummary,
  timeOptionsFor,
  timeZoneAbbr,
  timeZoneName,
} from "./jukeboxSchedule";

const DEFAULT_FOL_PRICE = 100;
const DEFAULT_SKIP_PRICE = 10;

const gold = "#FFEE20";
const goldBright = "#F5DC6A";
const textPrimary = "#E8E8EE";
const textMuted = "#9A9AAB";

const labelSx = {
  fontFamily: "var(--font-body)",
  color: textPrimary,
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.2px",
};

const helperSx = {
  fontFamily: "var(--font-body)",
  color: textMuted,
  fontSize: "11px",
  lineHeight: 1.5,
};

const SESSION_LABEL_MAX = 80;

/**
 * Host "Jukebox Settings" dialog: edit song-request tier pricing.
 * Controlled via { open, onClose }. Loads the host's own config on open.
 */
export default function JukeboxSettings({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [folPriceUsd, setFolPriceUsd] = useState(String(DEFAULT_FOL_PRICE));
  const [skipPriceUsd, setSkipPriceUsd] = useState(String(DEFAULT_SKIP_PRICE));
  const [freeEnabled, setFreeEnabled] = useState(false);
  const [nameYourPrice, setNameYourPrice] = useState(false);
  const [submissionsPaused, setSubmissionsPaused] = useState(false);
  // Request dates as { id, date: ISO string, label }, kept sorted soonest first.
  // `id` is the server's share-link id; null until the date has been saved.
  const [sessions, setSessions] = useState([]);
  const [hostId, setHostId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  // Submission counts per saved day id, and the day whose submissions are open.
  const [dayCounts, setDayCounts] = useState({});
  const [submissionsFor, setSubmissionsFor] = useState(null);
  // The date being composed: a local day, a 15-minute slot and an optional label.
  const [draftDate, setDraftDate] = useState("");
  const [draftTime, setDraftTime] = useState("");
  const [draftLabel, setDraftLabel] = useState("");

  useEffect(() => {
    if (!open) return;
    let active = true;
    const loadConfig = async () => {
      setLoading(true);
      try {
        const res = await ApiService.SONG_REQUEST_GET_CONFIG();
        if (!active) return;
        const cfg = res?.result || {};
        setFolPriceUsd(
          cfg.folPriceUsd != null ? String(cfg.folPriceUsd) : String(DEFAULT_FOL_PRICE)
        );
        setSkipPriceUsd(
          cfg.skipPriceUsd != null ? String(cfg.skipPriceUsd) : String(DEFAULT_SKIP_PRICE)
        );
        setFreeEnabled(Boolean(cfg.freeEnabled));
        setNameYourPrice(Boolean(cfg.nameYourPrice));
        setSubmissionsPaused(Boolean(cfg.submissionsPaused));
        setHostId(cfg.hostId || null);
        // Past dates are no use to fans, so only upcoming ones are shown and re-saved.
        const now = Date.now();
        setSessions(
          (Array.isArray(cfg.requestSessions) ? cfg.requestSessions : [])
            .filter((s) => s?.date && new Date(s.date).getTime() > now)
            .map((s) => ({
              id: s?.id || null,
              date: new Date(s.date).toISOString(),
              label: s?.label != null ? String(s.label) : "",
            }))
            .sort((a, b) => new Date(a.date) - new Date(b.date))
        );
        setDraftDate("");
        setDraftTime("");
        setDraftLabel("");
      } catch (error) {
        if (active) notifier.error(getErrorString(error));
      } finally {
        if (active) setLoading(false);
      }
    };
    // Counts are extra information; settings still work without them.
    const loadCounts = async () => {
      try {
        const res = await ApiService.SONG_REQUEST_HOST_DAYS();
        if (!active) return;
        const days = Array.isArray(res?.result?.days) ? res.result.days : [];
        setDayCounts(Object.fromEntries(days.map((d) => [d.id, d.counts])));
      } catch (error) {
        if (active) setDayCounts({});
      }
    };
    loadConfig();
    loadCounts();
    return () => {
      active = false;
    };
  }, [open]);

  const parsePrice = (value) => {
    const trimmed = String(value).trim();
    if (trimmed === "") return NaN;
    return Number(trimmed);
  };

  const now = new Date();
  const { min: minDate, max: maxDate } = dateInputBounds(now);
  const timeOptions = timeOptionsFor(draftDate, now);
  const draftInstant = combineLocal(draftDate, draftTime);
  const isDuplicate =
    draftInstant != null &&
    sessions.some((s) => sessionKey(s.date) === sessionKey(draftInstant));

  // Why the draft can't be added yet, or null when it's ready.
  let draftError = null;
  if (draftDate && (draftDate < minDate || draftDate > maxDate)) {
    draftError = "Pick a date between today and one year from now.";
  } else if (draftDate && timeOptions.length === 0) {
    draftError = "No times left today — pick a later date.";
  } else if (draftInstant && draftInstant.getTime() <= now.getTime()) {
    draftError = "That time has already passed — pick a later time.";
  } else if (isDuplicate) {
    draftError = "That date and time is already on your list.";
  }
  const canAdd = draftInstant != null && !draftError;

  const changeDraftDate = (value) => {
    setDraftDate(value);
    // Drop a time that no longer exists on the new day (e.g. earlier today).
    if (draftTime && !timeOptionsFor(value, new Date()).some((o) => o.value === draftTime)) {
      setDraftTime("");
    }
  };

  const addSession = () => {
    if (!canAdd) return;
    const entry = { id: null, date: draftInstant.toISOString(), label: draftLabel.trim() };
    setSessions((prev) =>
      [...prev, entry].sort((a, b) => new Date(a.date) - new Date(b.date))
    );
    setDraftTime("");
    setDraftLabel("");
  };

  const removeSession = (date) => {
    setSessions((prev) => prev.filter((s) => s.date !== date));
  };

  const shareSession = async (session) => {
    const when = formatSessionDate(session.date);
    try {
      const outcome = await shareLink({
        url: jukeboxDayUrl(hostId, session.id),
        title: "Jukebox day",
        text: `Request a song for ${when}${session.label ? ` — ${session.label}` : ""}`,
      });
      if (outcome === "copied") {
        setCopiedId(session.id);
        notifier.success("Link copied");
      }
    } catch (error) {
      notifier.error("Couldn't copy the link — try again");
    }
  };

  const handleSave = async () => {
    const fol = parsePrice(folPriceUsd);
    const skip = parsePrice(skipPriceUsd);

    // In name-your-price mode the fixed prices don't apply, so skip validating them.
    if (!nameYourPrice) {
      if (!Number.isFinite(fol) || fol < 0) {
        notifier.error("Front of Line price must be a number 0 or greater");
        return;
      }
      if (!Number.isFinite(skip) || skip < 0) {
        notifier.error("Skip the Line price must be a number 0 or greater");
        return;
      }
    }

    setSaving(true);
    try {
      // A date can pass while the dialog is open; leave those out.
      const saveTime = Date.now();
      // A complete draft the host forgot to Add is saved too, rather than lost.
      const pending = canAdd
        ? [{ date: draftInstant.toISOString(), label: draftLabel.trim() }]
        : [];
      const requestSessions = [...sessions, ...pending]
        .filter((s) => new Date(s.date).getTime() > saveTime)
        // Sending the id back keeps each date's share link the same.
        .map((s) => ({ ...(s.id ? { id: s.id } : {}), date: s.date, label: s.label }));

      const payload = {
        folPriceUsd: Number.isFinite(fol) ? fol : DEFAULT_FOL_PRICE,
        skipPriceUsd: Number.isFinite(skip) ? skip : DEFAULT_SKIP_PRICE,
        freeEnabled,
        nameYourPrice,
        submissionsPaused,
        requestSessions,
      };
      await ApiService.SONG_REQUEST_UPDATE_CONFIG(payload);
      notifier.success("Jukebox settings saved");
      onClose();
    } catch (error) {
      notifier.error(getErrorString(error));
    } finally {
      setSaving(false);
    }
  };

  const switchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: gold,
      "& + .MuiSwitch-track": {
        backgroundColor: gold,
        opacity: 0.55,
      },
    },
    "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
      boxShadow: `0 0 10px ${gold}, 0 0 4px rgba(255, 238, 32,0.9)`,
    },
  };

  // `premium` gives the $100 Front-of-Line tier a richer gold-tinted card.
  const priceFieldSx = (disabled, premium) => ({
    background: premium
      ? "linear-gradient(135deg, rgba(255, 238, 32,0.12), rgba(255, 238, 32,0.03))"
      : "rgba(255,255,255,0.03)",
    border: premium
      ? "1px solid rgba(255, 238, 32,0.5)"
      : "1px solid rgba(255,255,255,0.06)",
    boxShadow: premium ? "0 0 20px rgba(255, 238, 32,0.12)" : "none",
    borderRadius: "12px",
    px: 1.75,
    py: 1.25,
    opacity: disabled ? 0.4 : 1,
    transition: "opacity 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
  });

  const renderPriceRow = (label, value, onChange, premium = false) => (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={priceFieldSx(nameYourPrice, premium)}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        {premium && (
          <Box component="span" sx={{ fontSize: "14px", lineHeight: 1 }} aria-hidden>
            👑
          </Box>
        )}
        <Stack spacing={0.1}>
          <Typography sx={labelSx}>{label}</Typography>
          {premium && (
            <Typography
              sx={{
                fontFamily: "var(--font-body)",
                fontSize: "9px",
                fontWeight: 700,
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                color: goldBright,
              }}
            >
              Premium
            </Typography>
          )}
        </Stack>
      </Stack>
      <Stack direction="row" alignItems="baseline" spacing={0.25}>
        <Typography
          sx={{
            fontFamily: "var(--font-body)",
            color: gold,
            fontSize: "16px",
            fontWeight: 700,
          }}
        >
          $
        </Typography>
        <InputBase
          type="number"
          value={value}
          disabled={nameYourPrice}
          onChange={(e) => onChange(e.target.value)}
          inputProps={{ min: 0, step: "any", "aria-label": label }}
          sx={{
            width: 80,
            fontFamily: "var(--font-body)",
            color: gold,
            fontSize: "20px",
            fontWeight: 700,
            "& input": {
              textAlign: "right",
              p: 0,
              "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                WebkitAppearance: "none",
                margin: 0,
              },
              MozAppearance: "textfield",
            },
            "&.Mui-disabled input": {
              WebkitTextFillColor: "rgba(255, 238, 32,0.5)",
            },
          }}
        />
      </Stack>
    </Stack>
  );

  const renderToggleRow = (label, checked, onChange) => (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        background: "rgba(255,255,255,0.03)",
        border: checked
          ? "1px solid rgba(255, 238, 32,0.45)"
          : "1px solid rgba(255,255,255,0.06)",
        boxShadow: checked ? "0 0 18px rgba(255, 238, 32,0.1)" : "none",
        borderRadius: "12px",
        px: 1.75,
        py: 0.5,
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
      }}
    >
      <Typography sx={labelSx}>{label}</Typography>
      <Switch checked={checked} onChange={(e) => onChange(e.target.checked)} sx={switchSx} />
    </Stack>
  );

  // Shared styling for the datetime + description fields, echoing the gold price inputs.
  const fieldSx = {
    "& .MuiInputBase-root": {
      fontFamily: "var(--font-body)",
      color: textPrimary,
      fontSize: "13px",
      background: "rgba(255,255,255,0.04)",
      borderRadius: "8px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.1)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255, 238, 32,0.4)",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: gold,
    },
    "& input::placeholder": {
      color: textMuted,
      opacity: 1,
    },
    "& select option": {
      background: "#0C0C12",
      color: textPrimary,
    },
    // Tint the native calendar icon to suit the dark card.
    "& input[type='date']::-webkit-calendar-picker-indicator": {
      filter: "invert(1)",
      opacity: 0.6,
      cursor: "pointer",
    },
  };

  const sessionCardSx = {
    background: "linear-gradient(135deg, rgba(255, 238, 32,0.07), rgba(255, 238, 32,0.02))",
    border: "1px solid rgba(255, 238, 32,0.22)",
    borderRadius: "12px",
    px: 1.25,
    py: 1,
  };

  const renderSessionRow = (session) => (
    <Stack
      key={session.date}
      direction="row"
      alignItems="center"
      spacing={1}
      sx={sessionCardSx}
      data-testid="request-session"
    >
      <Stack spacing={0.1} sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography sx={labelSx}>{formatSessionDate(session.date)}</Typography>
        {session.label && (
          <Typography sx={{ ...helperSx, overflow: "hidden", textOverflow: "ellipsis" }}>
            {session.label}
          </Typography>
        )}
        {!(session.id && hostId) && (
          <Typography sx={helperSx}>Save to get a share link.</Typography>
        )}
        {session.id && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ pt: 0.25 }}>
            <Typography
              sx={{ ...helperSx, color: dayCounts[session.id]?.pending ? goldBright : textMuted }}
              data-testid="submission-badge"
            >
              {submissionSummary(dayCounts[session.id])}
            </Typography>
            <Button
              onClick={() => setSubmissionsFor(session.id)}
              size="small"
              sx={{
                minWidth: 0,
                p: 0,
                fontFamily: "var(--font-body)",
                color: gold,
                textTransform: "none",
                fontSize: "11px",
                fontWeight: 600,
                "&:hover": { background: "transparent", textDecoration: "underline" },
              }}
              aria-label={`View submissions for ${formatSessionDate(session.date)}`}
            >
              View submissions
            </Button>
          </Stack>
        )}
      </Stack>
      {session.id && hostId && (
        <Button
          onClick={() => shareSession(session)}
          size="small"
          startIcon={<IosShareIcon sx={{ fontSize: "16px !important" }} />}
          sx={{
            flexShrink: 0,
            fontFamily: "var(--font-body)",
            color: gold,
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 600,
            borderRadius: "8px",
            px: 1,
            "&:hover": { background: "rgba(255, 238, 32,0.08)" },
          }}
          aria-label={`Copy link for ${formatSessionDate(session.date)}`}
        >
          {copiedId === session.id ? "Copied" : "Copy link"}
        </Button>
      )}
      <IconButton
        onClick={() => removeSession(session.date)}
        size="small"
        sx={{
          color: textMuted,
          flexShrink: 0,
          "&:hover": { color: "#FF6B6B", background: "rgba(255,107,107,0.1)" },
        }}
        aria-label={`Remove ${formatSessionDate(session.date)}`}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Stack>
  );

  const renderSessionComposer = () => (
    <Stack spacing={1} sx={{ ...sessionCardSx, py: 1.25 }}>
      <Stack direction="row" spacing={1}>
        <TextField
          type="date"
          size="small"
          value={draftDate}
          onChange={(e) => changeDraftDate(e.target.value)}
          sx={{ ...fieldSx, flex: 1 }}
          inputProps={{ min: minDate, max: maxDate, "aria-label": "Request date" }}
        />
        <TextField
          select
          size="small"
          value={draftTime}
          onChange={(e) => setDraftTime(e.target.value)}
          disabled={!draftDate}
          SelectProps={{ native: true }}
          sx={{ ...fieldSx, flex: 1 }}
          inputProps={{ "aria-label": "Request time" }}
        >
          <option value="">Time</option>
          {timeOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </TextField>
      </Stack>
      <TextField
        size="small"
        fullWidth
        placeholder="Description (optional), e.g. Acoustic Night"
        value={draftLabel}
        onChange={(e) => setDraftLabel(e.target.value)}
        sx={fieldSx}
        inputProps={{ maxLength: SESSION_LABEL_MAX, "aria-label": "Description" }}
      />
      <Typography sx={helperSx}>
        Times are in your timezone: {timeZoneName()} ({timeZoneAbbr(draftInstant || now)}).
      </Typography>
      {draftError ? (
        <Typography role="alert" sx={{ ...helperSx, color: "#FF6B6B" }}>
          {draftError}
        </Typography>
      ) : (
        draftInstant && (
          <Typography sx={{ ...labelSx, color: goldBright }} data-testid="draft-summary">
            {formatSessionDate(draftInstant)}
            {draftLabel.trim() ? ` — ${draftLabel.trim()}` : ""}
          </Typography>
        )
      )}
      <Button
        onClick={addSession}
        disabled={!canAdd}
        variant="outlined"
        startIcon={<AddIcon />}
        sx={{
          alignSelf: "flex-start",
          fontFamily: "var(--font-body)",
          color: gold,
          borderColor: "rgba(255, 238, 32,0.4)",
          borderRadius: "10px",
          textTransform: "none",
          fontSize: "12px",
          fontWeight: 600,
          px: 2,
          "&:hover": {
            borderColor: gold,
            background: "rgba(255, 238, 32,0.08)",
          },
          "&.Mui-disabled": {
            color: "rgba(255, 238, 32,0.35)",
            borderColor: "rgba(255, 238, 32,0.15)",
          },
        }}
      >
        Add date
      </Button>
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        style: {
          background: "linear-gradient(180deg, #0C0C12 0%, #08080C 100%)",
          color: textPrimary,
          border: "1px solid rgba(255, 238, 32,0.18)",
          borderRadius: "16px",
          minWidth: 320,
          overflow: "hidden",
        },
      }}
    >
      {/* HUD signature: thin gold accent glow line at the top of the dialog. */}
      <Box
        sx={{
          height: "1px",
          background:
            "linear-gradient(90deg, transparent, rgba(255, 238, 32,0.6), transparent)",
        }}
      />
      <DialogTitle
        sx={{
          fontFamily: "var(--font-display)",
          fontSize: "22px",
          letterSpacing: "0.5px",
          color: gold,
          textTransform: "uppercase",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        Jukebox Settings
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: textMuted }}
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} sx={{ color: gold }} />
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {renderPriceRow("Front of Line price", folPriceUsd, setFolPriceUsd, true)}
            {renderPriceRow("Skip the Line price", skipPriceUsd, setSkipPriceUsd)}

            {renderToggleRow("Allow free requests", freeEnabled, setFreeEnabled)}
            {renderToggleRow("Let fans name their own price", nameYourPrice, setNameYourPrice)}

            {nameYourPrice && (
              <Typography sx={helperSx}>
                Fans set their own amount; the queue orders by how much they pay.
              </Typography>
            )}

            {renderToggleRow("Pause new submissions", submissionsPaused, setSubmissionsPaused)}
            <Typography sx={helperSx}>
              Temporarily stop accepting new song requests — your queue stays intact.
            </Typography>

            {/* Request dates: upcoming sessions fans can pick when submitting. */}
            <Box
              sx={{
                mt: 0.5,
                pt: 1.5,
                borderTop: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography sx={{ ...labelSx, fontSize: "14px" }}>Request dates</Typography>
                <Button
                  onClick={() => setSubmissionsFor("all")}
                  size="small"
                  sx={{
                    minWidth: 0,
                    p: 0,
                    fontFamily: "var(--font-body)",
                    color: gold,
                    textTransform: "none",
                    fontSize: "12px",
                    fontWeight: 600,
                    "&:hover": { background: "transparent", textDecoration: "underline" },
                  }}
                >
                  All submissions
                </Button>
              </Stack>
              <Typography sx={{ ...helperSx, mb: 1.5 }}>
                Add upcoming dates fans can request for (e.g. "Fri 8pm — Acoustic Night").
                Fans pick one; if their song isn't played within 24h of it, they're refunded.
              </Typography>

              <Stack spacing={1}>
                {sessions.map(renderSessionRow)}
                {renderSessionComposer()}
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", gap: "10px", pb: 2.5, px: 3 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            fontFamily: "var(--font-body)",
            color: gold,
            borderColor: "rgba(255, 238, 32,0.4)",
            borderRadius: "10px",
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 600,
            px: 3,
            "&:hover": {
              borderColor: gold,
              background: "rgba(255, 238, 32,0.08)",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading || saving}
          sx={{
            fontFamily: "var(--font-body)",
            background: `linear-gradient(135deg, ${goldBright} 0%, ${gold} 100%)`,
            color: "#0A0A0A",
            borderRadius: "10px",
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 700,
            px: 3,
            transition: "box-shadow 0.2s ease, transform 0.2s ease",
            "&:hover": {
              background: `linear-gradient(135deg, ${goldBright} 0%, ${gold} 100%)`,
              boxShadow: "0 6px 24px rgba(255, 238, 32,0.28)",
              transform: "translateY(-1px)",
            },
            "&.Mui-disabled": {
              color: "rgba(10,10,10,0.4)",
              background: "rgba(255, 238, 32,0.25)",
            },
          }}
        >
          {saving ? <CircularProgress size={18} sx={{ color: "#0A0A0A" }} /> : "Save"}
        </Button>
      </DialogActions>
      <JukeboxDaySubmissions
        open={Boolean(submissionsFor)}
        onClose={() => setSubmissionsFor(null)}
        initialSlotId={submissionsFor === "all" ? undefined : submissionsFor}
      />
    </Dialog>
  );
}
